import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, FileDown, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Billing = () => {
  const [bills, setBills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [creatingBill, setCreatingBill] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [activeSuggestField, setActiveSuggestField] = useState(null); // 'name' | 'phone' | null
  const suggestRef = useRef(null);
  const [formData, setFormData] = useState({
    patientId: '',
    consultationFee: '',
    items: [],
    paymentMode: 'Cash',
  });

  const fetchBills = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bills`);
      setBills(response.data);
    } catch (error) {
      console.error('Error fetching bills:', error);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  }, []);

  const fetchMedicines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/medicines`);
      setMedicines(response.data);
    } catch (error) {
      console.error('Error fetching medicines:', error);
    }
  }, []);

  const fetchProcedures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/procedures`);
      setProcedures(response.data);
    } catch (error) {
      console.error('Error fetching procedures:', error);
    }
  }, []);

  useEffect(() => {
    fetchBills();
    fetchPatients();
    fetchDoctors();
    fetchMedicines();
    fetchProcedures();
  }, [fetchBills, fetchPatients, fetchDoctors, fetchMedicines, fetchProcedures]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientPhone.trim()) {
      alert('Patient phone is required');
      return;
    }
    setCreatingBill(true);
    try {
      let patientId = formData.patientId;
      // If no existing patient selected, create one inline
      if (!patientId) {
        const payload = {
          name: patientName.trim(),
          phone: patientPhone.trim(),
        };
        const { data: newPatient } = await axios.post(`${API}/patients`, payload);
        patientId = newPatient.id;
        // refresh patient list so future suggestions include this one
        fetchPatients();
      }
      await axios.post(`${API}/bills`, { ...formData, patientId });
      fetchBills();
      resetForm();
      alert('Bill created successfully!');
    } catch (error) {
      console.error('Error creating bill:', error);
      alert(error.response?.data?.detail || 'Error creating bill');
    } finally {
      setCreatingBill(false);
    }
  };

  const addItem = (type) => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { type, itemId: '', itemName: '', quantity: 1, price: 0, total: 0, custom: false },
      ],
    });
  };

  const addCustomMedicine = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { type: 'medicine', itemId: '', itemName: '', quantity: 1, price: 0, total: 0, custom: true },
      ],
    });
  };

  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;

    if (field === 'itemId' && !updated[index].custom) {
      if (updated[index].type === 'medicine') {
        const medicine = medicines.find((m) => m.id === value);
        updated[index].itemName = medicine?.name || '';
        updated[index].price = medicine?.price || 0;
      } else if (updated[index].type === 'procedure') {
        const procedure = procedures.find((p) => p.id === value);
        updated[index].itemName = procedure?.name || '';
        updated[index].price = procedure?.price || 0;
      }
    }

    if (field === 'quantity' || field === 'price' || field === 'itemId') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].price) || 0;
      updated[index].total = qty * price;
    }

    setFormData({ ...formData, items: updated });
  };

  const removeItem = (index) => {
    const updated = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updated });
  };

  const calculateTotal = () => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    return itemsTotal + parseFloat(formData.consultationFee || 0);
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      consultationFee: '',
      items: [],
      paymentMode: 'Cash',
    });
    setPatientName('');
    setPatientPhone('');
    setActiveSuggestField(null);
    setShowForm(false);
  };

  // Autocomplete helpers
  const suggestions = useMemo(() => {
    if (!activeSuggestField) return [];
    const q = (activeSuggestField === 'name' ? patientName : patientPhone).trim().toLowerCase();
    if (!q) return patients.slice(0, 6);
    return patients
      .filter((p) =>
        (p.name || '').toLowerCase().includes(q) || (p.phone || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [patients, patientName, patientPhone, activeSuggestField]);

  const isExistingMatch = !!formData.patientId;
  const hasInput = patientName.trim() || patientPhone.trim();
  const showNewPatientHint = !isExistingMatch && hasInput;

  const selectPatient = (p) => {
    setPatientName(p.name || '');
    setPatientPhone(p.phone || '');
    setFormData((prev) => ({ ...prev, patientId: p.id }));
    setActiveSuggestField(null);
  };

  const onChangePatientName = (val) => {
    setPatientName(val);
    setActiveSuggestField('name');
    // any edit clears the selected patient binding
    if (formData.patientId) setFormData((prev) => ({ ...prev, patientId: '' }));
  };

  const onChangePatientPhone = (val) => {
    setPatientPhone(val);
    setActiveSuggestField('phone');
    if (formData.patientId) setFormData((prev) => ({ ...prev, patientId: '' }));
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setActiveSuggestField(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDownloadPdf = async (bill) => {
    try {
      const response = await axios.get(`${API}/bills/${bill.id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bill.billNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="billing-title">
            Billing
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Create and manage bills</p>
        </div>
        <button
          className="primary-btn flex items-center gap-2"
          onClick={() => setShowForm(true)}
          data-testid="create-bill-btn"
        >
          <Plus size={20} />
          Create Bill
        </button>
      </div>

      {showForm && (
        <div className="card p-6 mb-8" data-testid="billing-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Create New Bill
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="md:col-span-2 relative" ref={suggestRef}>
                <label>Patient (phone is required) <span style={{ color: '#EF4444' }}>*</span></label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="tel"
                      value={patientPhone}
                      onChange={(e) => onChangePatientPhone(e.target.value)}
                      onFocus={() => setActiveSuggestField('phone')}
                      placeholder="Phone number*"
                      required
                      data-testid="bill-patient-phone-input"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => onChangePatientName(e.target.value)}
                      onFocus={() => setActiveSuggestField('name')}
                      placeholder="Patient name (optional)"
                      data-testid="bill-patient-name-input"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {activeSuggestField && suggestions.length > 0 && (
                  <ul
                    className="absolute z-20 mt-1 w-full bg-white rounded-lg shadow-lg overflow-hidden"
                    style={{ border: '1px solid #E5E0D8', maxHeight: 240, overflowY: 'auto' }}
                    data-testid="patient-suggestions"
                  >
                    {suggestions.map((p) => (
                      <li
                        key={p.id}
                        onMouseDown={(e) => { e.preventDefault(); selectPatient(p); }}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex justify-between"
                        data-testid={`patient-suggestion-${p.id}`}
                      >
                        <span style={{ color: '#2D3A2A' }}>{p.name || <em style={{ color: '#9CA3AF' }}>No name</em>}</span>
                        <span style={{ color: '#6B7280' }}>{p.phone}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {showNewPatientHint && (
                  <div
                    className="mt-2 text-xs flex items-center gap-2 px-2 py-1.5 rounded"
                    style={{ backgroundColor: '#F5E6F5', color: '#990099' }}
                    data-testid="new-patient-hint"
                  >
                    <UserPlus size={14} />
                    New patient — will be created on submit
                  </div>
                )}
                {isExistingMatch && (
                  <div
                    className="mt-2 text-xs flex items-center gap-2 px-2 py-1.5 rounded"
                    style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                    data-testid="existing-patient-hint"
                  >
                    Existing patient selected
                  </div>
                )}
              </div>
              <div>
                <label>Consultation Fee (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.consultationFee}
                  onChange={(e) => setFormData({ ...formData, consultationFee: e.target.value })}
                  required
                  data-testid="bill-consultation-fee-input"
                />
              </div>
              <div>
                <label>Payment Mode</label>
                <select
                  value={formData.paymentMode}
                  onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                  data-testid="bill-payment-mode-select"
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => addItem('medicine')}
                  className="secondary-btn"
                  data-testid="add-medicine-item-btn"
                >
                  + Add Medicine
                </button>
                <button
                  type="button"
                  onClick={addCustomMedicine}
                  className="secondary-btn"
                  style={{ borderColor: '#990099', color: '#990099' }}
                  data-testid="add-custom-medicine-item-btn"
                  title="For combination/compounded medicines not in inventory"
                >
                  + Add Custom Medicine
                </button>
                <button
                  type="button"
                  onClick={() => addItem('procedure')}
                  className="secondary-btn"
                  data-testid="add-procedure-item-btn"
                >
                  + Add Procedure
                </button>
              </div>

              {formData.items.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4 p-4 rounded"
                  style={{ backgroundColor: item.custom ? '#F5E6F5' : '#F9F6F0' }}
                  data-testid={`bill-item-row-${index}`}
                >
                  <div>
                    <label>Type</label>
                    <input
                      type="text"
                      value={item.custom ? 'custom medicine' : item.type}
                      disabled
                    />
                  </div>
                  <div>
                    <label>{item.type === 'medicine' ? 'Medicine' : 'Procedure'}</label>
                    {item.custom ? (
                      <input
                        type="text"
                        value={item.itemName}
                        onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                        placeholder="e.g. Custom Kashayam mix"
                        required
                        data-testid={`bill-custom-name-${index}`}
                      />
                    ) : (
                      <select
                        value={item.itemId}
                        onChange={(e) => updateItem(index, 'itemId', e.target.value)}
                        required
                        data-testid={`bill-item-select-${index}`}
                      >
                        <option value="">Select {item.type}</option>
                        {item.type === 'medicine'
                          ? medicines.map((medicine) => (
                              <option key={medicine.id} value={medicine.id}>
                                {medicine.name}
                              </option>
                            ))
                          : procedures.map((procedure) => (
                              <option key={procedure.id} value={procedure.id}>
                                {procedure.name}
                              </option>
                            ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label>Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      required
                      data-testid={`bill-item-quantity-${index}`}
                    />
                  </div>
                  <div>
                    <label>Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.price}
                      onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                      disabled={!item.custom}
                      required={item.custom}
                      data-testid={`bill-item-price-${index}`}
                    />
                  </div>
                  <div>
                    <label>Total (₹)</label>
                    <input type="number" value={item.total} disabled data-testid={`bill-item-total-${index}`} />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2 rounded hover:bg-red-100 transition-colors"
                      data-testid={`remove-bill-item-${index}`}
                    >
                      <Trash2 size={18} style={{ color: '#EF4444' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded mb-6">
              <div className="flex justify-between items-center text-xl font-bold" style={{ color: '#2D3A2A' }}>
                <span>Total Amount:</span>
                <span style={{ color: '#990099' }} data-testid="bill-total-amount">₹{calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="submit" className="primary-btn" disabled={creatingBill} data-testid="bill-submit-btn">
                {creatingBill ? 'Creating...' : 'Create Bill'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="bill-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
          Recent Bills
        </h2>
        <div className="overflow-x-auto">
          <table data-testid="bills-table">
            <thead>
              <tr>
                <th>Bill Number</th>
                <th>Date</th>
                <th>Patient</th>
                <th>Total Amount</th>
                <th>Payment Mode</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((bill) => (
                <tr key={bill.id} data-testid={`bill-row-${bill.id}`}>
                  <td className="font-medium" style={{ color: '#990099' }}>{bill.billNumber}</td>
                  <td>{new Date(bill.date).toLocaleDateString()}</td>
                  <td className="font-medium" style={{ color: '#2D3A2A' }}>{bill.patientName || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>—</span>}</td>
                  <td className="font-bold" style={{ color: '#990099' }}>₹{bill.totalAmount.toFixed(2)}</td>
                  <td>{bill.paymentMode}</td>
                  <td>
                    <button
                      onClick={() => handleDownloadPdf(bill)}
                      className="secondary-btn text-sm flex items-center gap-2"
                      data-testid={`download-pdf-${bill.id}`}
                    >
                      <FileDown size={16} />
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {bills.length === 0 && (
            <div className="text-center py-8" style={{ color: '#6B7280' }} data-testid="no-bills">
              No bills created yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
